import { useTranslations } from 'next-intl'
import { ReactNode } from 'react'

type Amount = number | string
type Options = {
  locale?: string
  negative?: boolean
  unit?: (chunks: ReactNode) => ReactNode
}

export const formatCurrency = (amount: Amount, options?: Options) => {
  const defaultOptions = { locale: 'ko-KR', negative: false }
  const { locale, negative } = { ...defaultOptions, ...options }

  const number = typeof amount === 'string' ? Number(amount) : amount

  const formattedNumber = number.toLocaleString(locale)

  if (number > 0 && negative) {
    return `-${formattedNumber}`
  }

  return formattedNumber
}

export const useFormatCurrency = () => {
  const t = useTranslations('common')

  const format = (amount: Amount, options?: Options) => {
    const formattedValue = formatCurrency(amount, options)

    const isNegative = formattedValue.startsWith('-')

    if (isNegative) {
      return `-${t.rich('price', {
        price: formattedValue.slice(1),
        unit: options?.unit ?? (chunks => chunks),
      })}`
    }

    return t.rich('price', {
      price: formattedValue,
      unit: options?.unit ?? (chunks => chunks),
    })
  }

  return {
    formatCurrency: format,
  }
}

export const commaStringToNumber = (value: string): number => {
  return Number(value.replaceAll(/,/g, ''))
}

/**
 * 전화번호를 하이픈(-)으로 구분하여 포맷팅합니다.
 * @param phoneNumber - 포맷팅할 전화번호
 * @returns 포맷팅된 전화번호
 *
 * @example
 * formatPhoneNumber('0212345678') // '02-1234-5678'
 * formatPhoneNumber('01012345678') // '010-1234-5678'
 * formatPhoneNumber('1234567890') // '123-456-7890'
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return phoneNumber

  return phoneNumber.replace(
    /(^02.{0}|^01.{1}|[0-9]{3})([0-9]+)([0-9]{4})/,
    '$1-$2-$3',
  )
}
